import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaUsers, FaLightbulb, FaHandshake, FaRocket, FaUserCircle } from 'react-icons/fa';

// Import the image
import founderImage from '../assets/rishabh.jpeg';

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

// Core values data
const coreValues = [
  {
    icon: FaUsers,
    title: 'Community',
    description: 'Building strong connections and fostering collaboration among youth.'
  },
  {
    icon: FaLightbulb,
    title: 'Innovation',
    description: 'Encouraging creative thinking and innovative solutions.'
  },
  {
    icon: FaHandshake,
    title: 'Leadership',
    description: 'Developing future leaders through mentorship and guidance.'
  },
  {
    icon: FaRocket,
    title: 'Growth',
    description: 'Promoting continuous learning and personal development.'
  }
];

const AboutUs = () => {
  // State to handle image loading error
  const [imageError, setImageError] = useState(false);

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <>
      <Helmet>
        <title>About Us - YouthXConnect</title>
        <meta
          name="description"
          content="YouthXConnect is committed to fostering leadership, innovation, and service among young individuals."
        />
        <meta
          name="keywords"
          content="YouthXConnect, Youth Leadership, Innovation, Service, Youth Empowerment"
        />
        <meta property="og:title" content="About Us - YouthXConnect" />
        <meta
          property="og:description"
          content="YouthXConnect is committed to fostering leadership, innovation, and service among young individuals."
        />
        <meta property="og:image" content="%PUBLIC_URL%/about-us-og.png" />
        <meta property="og:url" content="https://www.youthxconnect.com/about-us" />
        <meta name="twitter:title" content="About Us - YouthXConnect" />
        <meta
          name="twitter:description"
          content="YouthXConnect is committed to fostering leadership, innovation, and service among young individuals."
        />
        <meta name="twitter:image" content="%PUBLIC_URL%/about-us-twitter.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <motion.section 
          className="relative bg-indigo-700 text-white py-20 sm:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              Empowering Youth, Inspiring Change
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl max-w-2xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Building a community of future leaders, innovators, and changemakers.
            </motion.p>
          </div>
        </motion.section>

        {/* Mission Section */}
        <motion.section 
          className="py-16 sm:py-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                YouthXConnect is dedicated to empowering young individuals through meaningful connections, 
                innovative learning experiences, and opportunities for personal growth. We believe in the 
                boundless potential of youth and their ability to shape a better future.
              </p>
            </motion.div>

            {/* Core Values Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((value, index) => (
                <motion.div
                  key={value.title}
                  variants={itemVariants}
                  className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="text-indigo-600 mb-4">
                    <value.icon size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Team Section */}
        <motion.section 
          className="bg-white py-16 sm:py-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 

            <motion.div 
              variants={itemVariants}
              className="flex flex-col items-center"
            >
              <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 mb-4 relative">
                {imageError ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <FaUserCircle className="w-full h-full text-gray-400" />
                  </div>
                ) : (
                  <img
                    src={founderImage}
                    alt="Rishabh Jain"
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                    loading="lazy"
                  />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Rishabh Jain</h3>
              <p className="text-gray-600 mb-4">Founder & CEO</p>
              <p className="text-gray-600 max-w-2xl text-center leading-relaxed">
                With a passion for youth empowerment and community building, Rishabh leads 
                YouthXConnect's mission to create meaningful opportunities for young individuals 
                to connect, learn, and grow together.
              </p>
            </motion.div>
          </div>
        </motion.section>
      </main>
    </>
  );
};

export default AboutUs;
